import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SectionCardProps {
  title: string
  action?: { label: string; to: string }
  headerRight?: React.ReactNode
  headerIcon?: React.ReactNode
  headerIconClassName?: string
  variant?: 'default' | 'panel' | 'chart'
  className?: string
  headerClassName?: string
  contentClassName?: string
  children: React.ReactNode
}

// Flat/Clean panel: white surface, hairline border, soft shadow.
export function SectionCard({
  title,
  action,
  headerRight,
  headerIcon,
  headerIconClassName,
  variant = 'default',
  className,
  headerClassName,
  contentClassName,
  children,
}: SectionCardProps) {
  if (variant === 'chart') {
    return (
      <section
        className={cn(
          'flex min-w-60 flex-col overflow-hidden rounded-[20px] border border-[#e7e9ef] bg-white shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]',
          className
        )}
      >
        <header
          className={cn(
            'flex min-h-17 shrink-0 items-center gap-2 overflow-hidden px-6 pt-6 pb-2',
            headerClassName
          )}
        >
          <div className='flex min-w-0 flex-1 items-center gap-3 overflow-hidden'>
            {headerIcon && (
              <span
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-lg',
                  headerIconClassName
                )}
              >
                {headerIcon}
              </span>
            )}
            <h2 className='truncate font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
              {title}
            </h2>
          </div>
          {headerRight && (
            <div className='shrink-0 font-sans text-xs font-medium leading-[1.4] text-[#3d434e]'>
              {headerRight}
            </div>
          )}
        </header>
        <div className={cn('flex flex-1 flex-col p-6', contentClassName)}>
          {children}
        </div>
      </section>
    )
  }

  if (variant === 'panel') {
    return (
      <section
        className={cn(
          'flex min-w-60 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm',
          className
        )}
      >
        <header className='flex h-12 shrink-0 items-center pr-6 pt-4'>
          <div className='flex flex-1 items-center border-l-4 border-primary px-6 py-1'>
            <h2 className='text-base font-semibold leading-tight'>{title}</h2>
          </div>
          {headerRight}
        </header>
        <div className={cn('flex flex-1 flex-col p-4', contentClassName)}>
          {children}
        </div>
        {action && (
          <Link
            to={action.to}
            className='inline-flex items-center justify-center gap-2 px-4 pb-4 text-base text-primary hover:underline'
          >
            {action.label} <ArrowRight className='h-4 w-4' />
          </Link>
        )}
      </section>
    )
  }

  return (
    <section
      className={cn(
        'flex flex-col rounded-lg border border-border bg-card p-4 shadow-sm',
        className
      )}
    >
      <header className='mb-4 flex items-center justify-between'>
        <h2 className='text-base font-semibold'>{title}</h2>
        {headerRight}
      </header>
      <div className='flex-1'>{children}</div>
      {action && (
        <Link
          to={action.to}
          className='mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline'
        >
          {action.label} <ArrowRight className='h-4 w-4' />
        </Link>
      )}
    </section>
  )
}

export default SectionCard
